import { useAppSelector } from '@/hooks'
import { ID } from '@/types/model'
import { VehicleChargingDelayEvent } from '@/types'
import { dateIsBetween } from '@/utils/time'
import { getDateFromTimestamp } from '@/utils/timeUtilities'
import { selectNextVehicleDREvent } from '@/selectors/vehicleDemandResponseEvent'
import { DeviceType } from '@/app/components/DeviceSelectionGallery/DeviceSelectionGallery.types'
import { selectNextDeviceDREvent } from '@/selectors/vehicleDemandResponseEvent/selectNextVehicleDREvent'
import { match } from 'ts-pattern'

export const mockEventExplanation =
  'You are currently participating in an event until 9:30 PM tonight. Your vehicle charging may be paused during this time.'

function useEventDetails(event: VehicleChargingDelayEvent | undefined) {
  if (!event) {
    return undefined
  }
  const formattedStartTime = getDateFromTimestamp(
    event.start_datetime,
    'h:mm A',
  )
  const formattedEndTime = getDateFromTimestamp(event.end_datetime, 'h:mm A')
  const endIsInToday =
    new Date(event.end_datetime).getDate() === new Date().getDate()

  const eventIsCurrentlyActive = dateIsBetween({
    date: new Date(),
    start: event?.start_datetime,
    end: event?.end_datetime,
  })

  return {
    formattedStartTime,
    formattedEndTime,
    endIsInToday,
    eventIsCurrentlyActive,
    event,
  }
}

export function useDeviceEventExplanation(
  deviceId: ID,
  deviceType: DeviceType,
) {
  const nextEvent = useAppSelector((state) =>
    selectNextDeviceDREvent(state, deviceId, deviceType),
  )

  const eventDetails = useEventDetails(nextEvent)

  if (!eventDetails) {
    return 'No Upcoming Events'
  }

  const {
    formattedStartTime,
    formattedEndTime,
    endIsInToday,
    eventIsCurrentlyActive,
    event,
  } = eventDetails

  const deviceTypeString = match(deviceType)
    .with('vehicle', () => 'EV')
    .with('vehicleCharger', () => 'EV')
    .otherwise(() => 'device')

  // State 1: Currently opted in + event is upcoming
  if (!eventIsCurrentlyActive && event.enrolled) {
    return `You have an upcoming event from ${formattedStartTime} to ${formattedEndTime}.`
  }

  // State 2: Currently opted in + event is live
  if (eventIsCurrentlyActive && event.enrolled) {
    return `You're currently participating in an event from ${formattedStartTime} to ${formattedEndTime}. Your ${deviceTypeString} charging may be paused momentarily to reduce impact on the grid.`
  }

  // State 3: Currently opted out + event is upcoming
  if (!eventIsCurrentlyActive && !event.enrolled) {
    return `There is an upcoming event from ${formattedStartTime} to ${formattedEndTime}. Your ${deviceTypeString} charging may be paused if you would like to participate.`
  }

  // State 4: Currently opted out + event is live
  return `There is an ongoing event from ${formattedStartTime} to ${formattedEndTime}. Your ${deviceTypeString} charging may be paused if you would like to participate.`
}

export function useEnrolledEventDetails(vehicleId: ID) {
  const nextEvent = useAppSelector((state) =>
    selectNextVehicleDREvent(state, vehicleId),
  )

  const eventDetails = useEventDetails(nextEvent)

  if (!eventDetails || !eventDetails?.event.enrolled) {
    return {
      title: 'There was an issue retrieving your event details.',
      details: 'Please try again later.',
    }
  }

  const { formattedStartTime, formattedEndTime, eventIsCurrentlyActive } =
    eventDetails

  if (eventIsCurrentlyActive) {
    return {
      title: 'Great! Thanks for participating in this event.',
      details: `This event lasts until ${formattedEndTime}. Your vehicle charging may be paused during this time.`,
    }
  }

  return {
    title: 'Great! Thanks for participating in the upcoming event.',
    details: `This event starts at ${formattedStartTime}. Your vehicle charging may be paused during this time.`,
  }
}
